import { Link, Skeleton, SxProps, Typography } from "@mui/material";
import { HTMLAttributeAnchorTarget, memo } from "react";
import { Box, Icon } from "..";
import { Styles, gradient, palette } from "../../../styles/theme";
import { useGlobalLoading } from "../../../utils/ContextHooks";
import { MaterialSymbolType } from "../Icon/IconConstant";
import { useThemeBreakpoints } from "../../../utils/themeUtils";

const styles: Styles = {
  container: {
    p: { xs: "16px", lg: "24px 36px 24px 24px" },
    borderRadius: "12px",
    border: `1px solid ${palette.grey.midGrey}`,
    display: "flex",
    flex: { xs: "1 1 50%", sm: "unset" },
    gap: 1.5,
    cursor: "pointer",
    alignItems: "center",
    minHeight: { xs: "66px", lg: "72px" },
    maxHeight: { xs: "66px", lg: "72px" },
    boxShadow: "2px 2px 4px 0px rgba(236, 236, 236, 0.40)",
    "&:hover": {
      background: `${gradient.backgroundGradient},
        ${palette.borderGradientHover}`,
      border: "1px solid transparent",
    },
    "&:active": {
      // minus one on the padding to prevent content shifting cause be the increase of 1 px of border
      p: { xs: "15px", lg: "23px 35px 23px 23px" },
      background: palette.borderGradient,
      border: "2px solid transparent",
      ":hover": {
        background: `${gradient.backgroundGradient},
        ${palette.borderGradient}`,
        border: "2px solid transparent",
      },
    },
    textDecoration: "none",
  },
  mainText: {
    fontSize: { xs: "14px", lg: "16px" },
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    color: palette.grey.primary,
  },
  skeleton: {
    display: "flex",
    flex: { xs: "1 1 50%", sm: "unset" },
    width: {
      xs: 150,
      lg: 250,
    },
    p: { xs: "16px", lg: "24px 36px 24px 24px" },
    minHeight: { xs: "66px", lg: "100px" },
  },
};

interface Props {
  text: string;
  subtext?: string;
  icon: MaterialSymbolType;
  to: string;
  target: HTMLAttributeAnchorTarget;
  textSx?: SxProps;
  sx?: SxProps;
}

const GradientLink = (props: Props) => {
  const { text, subtext, icon, to, target, textSx, sx } = props;
  const { isGloballyLoading } = useGlobalLoading();
  const { isMobile } = useThemeBreakpoints();

  return isGloballyLoading ? (
    <Skeleton variant="rounded" sx={styles.skeleton} />
  ) : (
    <Link
      sx={[styles.container, ...(Array.isArray(sx) ? sx : [sx])]}
      href={to}
      target={target}
    >
      <Icon
        icon={icon}
        size={isMobile ? 16 : 24}
        color={palette.grey.primary}
      />
      <Box>
        <Typography
          variant={isMobile ? "textRegular" : "actionBigIcon"}
          color={palette.grey.primary}
          sx={textSx}
        >
          {text}
        </Typography>
        {subtext && <Typography>{subtext}</Typography>}
      </Box>
    </Link>
  );
};

export default memo(GradientLink);
